import { useCallback, useEffect, useMemo } from 'react';

import { NavmenuProps } from '@/components/atomic/templates/navigation/navmenu/typings';
import useCanUseSavedBuilds from '@/hooks/use-can-use-save-builds';
import useSaveBuildsUserId from '@/hooks/use-save-builds-user-id';
import useStore from '@/state';
import setSavedBuildCountsAction from '@/state/selectors/saved-builds/set-build-counts';
import setSavedBuildDataAction from '@/state/selectors/saved-builds/set-build-data';
import SaveBuilds from '@connectjlr/save-builds';
import { EnvVarsService } from '@multitenancy-spa/env-vars-service/esm/services/env-vars-service';

const useSavedBuilds = ({
	locale = 'xi',
	scsApiUrl = 'https://buildyour.landrover.com',
}: Pick<NavmenuProps, 'locale' | 'scsApiUrl'>) => {
	const market = useMemo(() => {
		const localeParts = locale.split('_');
		if (localeParts.length === 1) return localeParts[0].toLowerCase();
		if (localeParts.length > 1)
			return localeParts[localeParts.length - 1].toLowerCase();
		return '';
	}, [locale]);

	const canUseSaveBuildsApi = useCanUseSavedBuilds();

	const setSavedBuildCounts = useStore(setSavedBuildCountsAction);
	const setSavedBuildData = useStore(setSavedBuildDataAction);

	const userId = useSaveBuildsUserId();

	const saveBuilds = useMemo(() => {
		if (!scsApiUrl) return null;
		return SaveBuilds({
			environment: scsApiUrl,
			userId,
		});
	}, [scsApiUrl, userId]);

	const setupBuildsFromConnectApi = useCallback(async () => {
		try {
			const brand = EnvVarsService.getVar('VITE_BRAND_SHORT');
			if (!saveBuilds || !market || !brand) return null;
			const response = await saveBuilds.get({ brand, market });
			const data = await response.json();
			setSavedBuildData(data);
		} catch {}
	}, [market, saveBuilds, setSavedBuildData]);

	const setupBuildsFromCookies = useCallback(() => {
		const buildCounts = globalThis.sessionStorage.getItem('buildCounts');

		if (buildCounts) {
			try {
				const parsedBuildCounts = JSON.parse(buildCounts);
				if (parsedBuildCounts) setSavedBuildCounts(parsedBuildCounts);
			} catch {}
		}
	}, [setSavedBuildCounts]);

	const handleSaveBuildsUpdate = useCallback(
		(e: CustomEvent) => {
			if (e.detail.action === 'items-updated') {
				setupBuildsFromConnectApi();
				setupBuildsFromCookies();
			}
		},
		[setupBuildsFromConnectApi, setupBuildsFromCookies],
	);

	useEffect(() => {
		if (canUseSaveBuildsApi) {
			setupBuildsFromConnectApi();
			setupBuildsFromCookies();
		}
	}, [
		canUseSaveBuildsApi,
		setupBuildsFromConnectApi,
		setupBuildsFromCookies,
	]);

	useEffect(() => {
		if (canUseSaveBuildsApi) {
			globalThis.addEventListener(
				'SavedBuildsCardEvent',
				handleSaveBuildsUpdate,
			);
		}

		return () => {
			if (canUseSaveBuildsApi) {
				globalThis.removeEventListener(
					'SavedBuildsCardEvent',
					handleSaveBuildsUpdate,
				);
			}
		};
	}, [canUseSaveBuildsApi, handleSaveBuildsUpdate]);
};

export default useSavedBuilds;
